import { useState, useRef, useEffect, useCallback } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { useAIPreferences } from '@/contexts/AIPreferencesContext';
import { useGuestDemo } from '@/contexts/GuestDemoContext';
import { useAuth } from '@/contexts/AuthContext';
import { useAd } from '@/contexts/AdContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useKeyboard } from '@/hooks/useKeyboard';
import { useTutorPrefill } from '@/pages/home/useTutorPrefill';
import { motion, AnimatePresence, useReducedMotion } from 'motion/react';
import FormattedMessage from './components/FormattedMessage';
import ExpandableMenu from './components/ExpandableMenu';
import LearnMenu from './components/LearnMenu';
import DemoGate from '@/components/feature/DemoGate';
import { sendAIChatMessage } from '@/lib/ai';
import { getStudyScope, isStudyScopeCurrent } from '@/lib/studyStorage';
import {
  appendChatMessage,
  createChatConversation,
  generateChatTitle,
  getChatConversation,
  type ChatMessage,
  type LessonChatContext,
} from '@/lib/chatHistory';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
}

function displayTime(iso = new Date().toISOString()): string {
  return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function toDisplayMessage(message: ChatMessage): Message {
  return {
    id: message.id,
    role: message.role,
    content: message.content,
    timestamp: displayTime(message.createdAt),
  };
}

const AD_CHAT_BONUS_KEY = 'lavyn_ad_chat_bonus';
const AD_BONUS_MESSAGES = 3;

function hasActiveAdBonus(): boolean {
  try {
    const raw = sessionStorage.getItem(AD_CHAT_BONUS_KEY);
    if (!raw) return false;
    const data = JSON.parse(raw) as { used: number; granted: number };
    return data.used < data.granted;
  } catch { return false; }
}

function consumeAdBonus(): boolean {
  try {
    const raw = sessionStorage.getItem(AD_CHAT_BONUS_KEY);
    if (!raw) return false;
    const data = JSON.parse(raw) as { used: number; granted: number };
    if (data.used >= data.granted) return false;
    data.used += 1;
    sessionStorage.setItem(AD_CHAT_BONUS_KEY, JSON.stringify(data));
    return true;
  } catch { return false; }
}

function grantAdBonus() {
  sessionStorage.setItem(AD_CHAT_BONUS_KEY, JSON.stringify({ used: 0, granted: AD_BONUS_MESSAGES }));
}

export default function AIAssistant() {
  const { toggleTheme, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  const reduceMotion = useReducedMotion();
  const { preferences } = useAIPreferences();
  const { canAIChat, useAIChat, activeGate, dismissGate } = useGuestDemo();
  const { isAuthenticated, isRehydrated, user } = useAuth();
  const { watchAd } = useAd();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [messages, setMessages] = useState<Message[]>([]);
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null);
  const [conversationTitle, setConversationTitle] = useState('New AI chat');
  const [lessonContext, setLessonContext] = useState<LessonChatContext | undefined>();
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [requestError, setRequestError] = useState('');
  const [copiedMessageId, setCopiedMessageId] = useState<string | null>(null);
  const [copyError, setCopyError] = useState('');
  const [showAdPrompt, setShowAdPrompt] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const isNearBottomRef = useRef(true);
  const keyboard = useKeyboard();

  const consumeAIChat = useAIChat;

  const resetConversation = useCallback(() => {
    setActiveConversationId(null);
    setConversationTitle('New AI chat');
    setLessonContext(undefined);
    setMessages([]);
    setInput('');
    setRequestError('');
    setCopyError('');
    setCopiedMessageId(null);
    setSearchParams({}, { replace: true });
  }, [setSearchParams]);

  useTutorPrefill(setInput, resetConversation);

  useEffect(() => {
    if (!isRehydrated) return;
    const requestedConversationId = searchParams.get('conversation');
    if (!requestedConversationId) return;
    const conversation = getChatConversation(requestedConversationId, user?.id);
    if (!conversation) {
      resetConversation();
      return;
    }
    setActiveConversationId(conversation.id);
    setConversationTitle(conversation.title);
    setLessonContext(conversation.lessonContext);
    setRequestError('');
    if (conversation.messages.length > 0) {
      setMessages(conversation.messages.map(toDisplayMessage));
    } else {
      setMessages([]);
    }
  }, [resetConversation, searchParams, user?.id, isRehydrated]);

  // Show ad prompt when demo limit is reached and no active bonus
  useEffect(() => {
    if (!canAIChat && !hasActiveAdBonus() && !isAuthenticated) {
      setShowAdPrompt(true);
    } else {
      setShowAdPrompt(false);
    }
  }, [canAIChat, isAuthenticated]);

  const unlockChatWithAd = () => {
    watchAd(
      'ai_chat_bonus',
      'More AI Messages',
      `unlock ${AD_BONUS_MESSAGES} additional AI messages`,
      () => {
        grantAdBonus();
        setShowAdPrompt(false);
      }
    );
  };

  const handleSendMessage = useCallback(async (text: string, retry = false) => {
    if (!text.trim() || isTyping) return;
    const owner = getStudyScope();
    if (!owner.ready || owner.ownerId !== (user?.id || null)) return;

    // Check demo limit before sending
    if (!retry && !consumeAIChat()) {
      // Try to consume an ad-granted bonus message
      if (consumeAdBonus()) {
        // proceed below
      } else {
        setShowAdPrompt(true);
        return;
      }
    }

    const userMsg: Message = retry ? messages.filter((message) => message.role === 'user').at(-1)! : {
      id: `u-${Date.now()}`,
      role: 'user',
      content: text.trim(),
      timestamp: displayTime(),
    };

    if (!userMsg) return;
    const previousMessages = messages.filter((message) => message.id !== 'welcome-greeting' && message.id !== userMsg.id);
    if (!retry) setMessages((current) => [...current, userMsg]);
    setIsTyping(true);
    setRequestError('');
    isNearBottomRef.current = true;

    let conversationId = activeConversationId;
    if (!conversationId) {
      const storedUserMessage: ChatMessage = {
        id: userMsg.id,
        role: 'user',
        content: userMsg.content,
        createdAt: new Date().toISOString(),
      };
      const conversation = createChatConversation({
        title: generateChatTitle(userMsg.content),
        source: lessonContext ? 'lesson' : 'main-tutor',
        lessonContext,
        messages: [storedUserMessage],
      }, user?.id, owner);
      conversationId = conversation.id;
      setActiveConversationId(conversation.id);
      setConversationTitle(conversation.title);
      setSearchParams({ conversation: conversation.id }, { replace: true });
    } else if (!retry) {
      appendChatMessage(conversationId, {
        id: userMsg.id,
        role: 'user',
        content: userMsg.content,
      }, user?.id, owner);
      if (conversationTitle === 'New AI chat') {
        setConversationTitle(generateChatTitle(userMsg.content));
      }
    }

    try {
      const response = await sendAIChatMessage({
        messages: [...previousMessages, userMsg].map((message) => ({
          role: message.role,
          content: message.content,
        })),
        preferences: {
          tutorMode: preferences.tutorMode,
          learningStyle: preferences.learningStyle,
          explanationStyle: preferences.explanationStyle,
          learningMode: preferences.learningMode,
        },
        context: lessonContext ? {
          title: lessonContext.lessonTitle,
          subject: lessonContext.subject,
          topic: lessonContext.topic,
          summary: lessonContext.summary,
          keyPoints: lessonContext.keyPoints,
          narration: lessonContext.narration,
        } : undefined,
      });
      const aiMsg: Message = {
        id: `a-${Date.now()}`,
        role: 'assistant',
        content: response,
        timestamp: displayTime(),
      };
      appendChatMessage(conversationId, {
        id: aiMsg.id,
        role: 'assistant',
        content: aiMsg.content,
      }, user?.id, owner);
      if (isStudyScopeCurrent(owner)) setMessages((prev) => [...prev, aiMsg]);
    } catch (error) {
      if (isStudyScopeCurrent(owner)) setRequestError(error instanceof Error ? error.message : 'The tutor could not answer. Please try again.');
    } finally {
      if (isStudyScopeCurrent(owner)) setIsTyping(false);
    }
  }, [
    activeConversationId,
    consumeAIChat,
    conversationTitle,
    isTyping,
    lessonContext,
    messages,
    preferences.explanationStyle,
    preferences.learningMode,
    preferences.learningStyle,
    preferences.tutorMode,
    setSearchParams,
    user?.id,
  ]);

  const handleSend = () => {
    if (!input.trim() || isTyping || isInputDisabled) return;
    handleSendMessage(input);
    setInput('');
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  };

  // Auto-scroll to bottom on new messages
  const scrollToBottom = useCallback(() => {
    const el = scrollRef.current;
    if (!el || !isNearBottomRef.current) return;
    el.scrollTo({ top: el.scrollHeight, behavior: reduceMotion ? 'auto' : 'smooth' });
  }, [reduceMotion]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping, scrollToBottom]);

  // Scroll to bottom when keyboard opens
  useEffect(() => {
    if (keyboard.isOpen) {
      const t1 = setTimeout(() => scrollToBottom(), 100);
      const t2 = setTimeout(() => scrollToBottom(), 350);
      return () => { clearTimeout(t1); clearTimeout(t2); };
    }
  }, [keyboard.isOpen, scrollToBottom]);

  // Track scroll position to know if user is reading history
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const handleScroll = () => {
      const threshold = 80;
      isNearBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < threshold;
    };
    el.addEventListener('scroll', handleScroll, { passive: true });
    return () => el.removeEventListener('scroll', handleScroll);
  }, []);

  // Auto-resize textarea
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
  }, [input]);

  // Dynamic suggested prompts based on tutor mode and learning style
  const getSuggestedPrompts = () => {
    if (preferences.tutorMode === 'encouraging') {
      return ['Help me get started', 'Explain a tricky idea', 'Check my working'];
    }
    if (preferences.tutorMode === 'direct') {
      return ['Explain a formula', 'Check my working', 'Practise an exam question'];
    }
    if (preferences.learningStyle === 'Visual') {
      return ['Explain with a diagram', 'Help me read a graph', 'Show a worked example'];
    }
    if (preferences.learningStyle === 'Exam Prep') {
      return ['Practise an exam question', 'Check my method', 'Explain a common mistake'];
    }
    if (preferences.explanationStyle === 'Exam Style') {
      return ['Practise an exam question', 'Check my method', 'Explain a common mistake'];
    }
    if (preferences.explanationStyle === 'Step-by-Step') {
      return ['Work through a problem', 'Explain a tricky idea', 'Check my working'];
    }
    if (preferences.explanationStyle === 'Simple') {
      return ['Explain a concept simply', 'Summarise my notes', 'Show a small example'];
    }
    if (preferences.learningMode === 'Homework Help') {
      return ['Help me start a problem', 'Check my working', 'Explain my mistake'];
    }
    if (preferences.learningMode === 'Exam Revision') {
      return ['Revise a topic', 'Practise an exam question', 'Check my method'];
    }
    return ['Explain a tricky idea', 'Try a practice question', 'Show a real-world example'];
  };

  const suggestedPrompts = getSuggestedPrompts();

  const bgClass = isDark ? 'bg-[#101011]' : 'bg-[#f0f0ef]';
  const inputBg = isDark ? 'bg-[#1C1D21]' : 'bg-black/[0.04]';
  const inputBorder = isDark ? 'border-white/[0.05]' : 'border-black/[0.05]';
  const inputBorderFocus = isDark ? 'focus-within:border-white/12 focus-within:bg-[#222226]' : 'focus-within:border-black/10 focus-within:bg-black/[0.06]';
  const placeholderColor = isDark ? 'placeholder-[#6E6E73]' : 'placeholder-black/25';
  const userBubbleBg = isDark ? 'bg-[#2A2A2E]' : 'bg-[#e5e5e5]';
  const userTextColor = isDark ? 'text-[#F5F5F7]' : 'text-black';
  const suggestedBg = isDark ? 'bg-[#1C1D21]' : 'bg-black/[0.04]';
  const suggestedHover = isDark ? 'hover:bg-[#222226]' : 'hover:bg-black/[0.08]';
  const suggestedText = isDark ? 'text-[#6E6E73]' : 'text-black/45';
  const suggestedBorder = isDark ? 'border-white/[0.04]' : 'border-black/[0.04]';
  const disclaimerText = isDark ? 'text-[#48484F]' : 'text-black/20';
  const sendInactiveBg = isDark ? 'bg-[#2A2A2E] text-[#48484F]' : 'bg-black/[0.06] text-black/20';
  const typingDot = isDark ? 'bg-[#48484F]' : 'bg-black/20';

  // AI message container styles
  const aiContainerBg = isDark ? 'bg-[#141415]/60' : 'bg-white/60';
  const aiContainerBorder = isDark ? 'border-white/[0.04]' : 'border-black/[0.04]';

  const tutorLabel = preferences.tutorMode === 'patient' ? 'Patient Tutor'
    : preferences.tutorMode === 'direct' ? 'Direct Tutor'
    : 'Encouraging Tutor';

  const isInputDisabled = !isRehydrated || (!canAIChat && !hasActiveAdBonus() && !isAuthenticated);

  return (
    <div className={`study-tutor flex flex-col h-full min-h-0 ${bgClass} ${isDark ? 'text-[#efefef]' : 'text-neutral-900'}`} data-theme={resolvedTheme}>
      {/* Header */}
      <div className="tutor-header flex-shrink-0">
        <LearnMenu theme={resolvedTheme} onNewChat={resetConversation} onToggleTheme={toggleTheme} busy={isTyping} context={lessonContext?.lessonTitle || (conversationTitle === 'New AI chat' ? `${preferences.learningStyle} · ${tutorLabel}` : conversationTitle)} />
      </div>

      {/* Messages scroll area */}
      <div
        ref={scrollRef}
        className="tutor-scroll flex-1 min-h-0 overflow-y-auto px-5 pt-4 pb-6 no-scrollbar overscroll-y-contain lg:px-8"
        style={{
          WebkitOverflowScrolling: 'touch',
          scrollPaddingBottom: '100px',
          scrollBehavior: reduceMotion ? 'auto' : 'smooth',
        }}
      >
        {lessonContext && (
          <div className="mb-6 border-l-2 border-neutral-500/20 px-4 py-1 lg:mx-auto lg:max-w-3xl">
            <div className="flex items-start gap-3">
              <div className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center text-neutral-500">
                <i className="ri-book-open-line text-sm" />
              </div>
              <div className="min-w-0">
                <p className="text-[10px] font-medium uppercase tracking-[0.14em] text-neutral-500">Studying from your lesson</p>
                <h2 className="mt-1 text-[14px] font-semibold">{lessonContext.lessonTitle}</h2>
                <p className="mt-1 text-[12px] leading-relaxed text-[#A1A1A6]">
                  {[lessonContext.subject, lessonContext.topic].filter(Boolean).join(' · ')}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Demo hint — guests only */}
        {!canAIChat && !isAuthenticated && !hasActiveAdBonus() && (
          <div className={`mb-6 px-4 py-3 rounded-xl ${isDark ? 'bg-blue-500/[0.05] border border-blue-500/10' : 'bg-blue-50 border border-blue-100'} flex items-center gap-2`}>
            <div className="w-4 h-4 flex items-center justify-center">
              <i className={`ri-information-line ${isDark ? 'text-blue-400' : 'text-blue-500'} text-sm`} />
            </div>
            <p className={`text-[11px] ${isDark ? 'text-blue-300' : 'text-blue-700'}`}>
              Demo chat used.{' '}
              <button onClick={() => navigate('/auth/signup')} className="font-semibold underline cursor-pointer">
                Create an account
              </button>{' '}
              for unlimited AI tutoring.
            </p>
          </div>
        )}

        {/* Ad bonus active indicator */}
        {hasActiveAdBonus() && (
          <div className={`mb-6 px-4 py-2 rounded-xl ${isDark ? 'bg-emerald-500/[0.05] border border-emerald-500/10' : 'bg-emerald-50 border border-emerald-100'} flex items-center gap-2`}>
            <div className="w-4 h-4 flex items-center justify-center">
              <i className={`ri-gift-line ${isDark ? 'text-emerald-400' : 'text-emerald-600'} text-sm`} />
            </div>
            <p className={`text-[11px] ${isDark ? 'text-emerald-300' : 'text-emerald-700'}`}>
              Bonus messages active from ad reward.
            </p>
          </div>
        )}

        {/* Ad prompt — inline rewarded ad offer */}
        <AnimatePresence>
          {showAdPrompt && (
            <motion.div
              className={`mb-6 rounded-2xl border ${isDark ? 'border-emerald-500/15 bg-emerald-500/[0.03]' : 'border-emerald-200 bg-emerald-50/[0.5]'} p-4`}
              initial={{ opacity: 0, y: 12, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -8, scale: 0.98 }}
              transition={{ duration: 0.35, ease: [0.32, 0.72, 0, 1] }}
            >
              <div className="flex items-start gap-3">
                <div className={`w-10 h-10 rounded-xl ${isDark ? 'bg-emerald-500/10' : 'bg-emerald-100'} flex items-center justify-center flex-shrink-0`}>
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className={`ri-movie-ai-line ${isDark ? 'text-emerald-400' : 'text-emerald-600'} text-lg`} />
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className={`text-sm font-semibold ${isDark ? 'text-emerald-300' : 'text-emerald-700'} mb-1`}>
                    Keep Learning Free
                  </h4>
                  <p className={`text-[11px] ${isDark ? 'text-emerald-400/80' : 'text-emerald-700/80'} leading-relaxed mb-3`}>
                    Sign in to continue your study session with LAVYN Tutor.
                  </p>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => navigate('/auth/login')}
                      className={`px-4 py-2 rounded-xl text-xs font-semibold ${isDark ? 'bg-emerald-500/10 text-emerald-300 border border-emerald-500/20' : 'bg-emerald-100 text-emerald-700 border border-emerald-200'} cursor-pointer whitespace-nowrap`}
                    >
                      <div className="flex items-center gap-1.5">
                        <div className="w-3.5 h-3.5 flex items-center justify-center">
                          <i className="ri-play-circle-line text-[10px]" />
                        </div>
                        Sign in to continue
                      </div>
                    </button>
                    <button
                      onClick={() => navigate('/auth/signup')}
                      className={`px-4 py-2 rounded-xl text-xs font-medium ${isDark ? 'text-emerald-400/70 hover:text-emerald-300' : 'text-emerald-700/70 hover:text-emerald-800'} cursor-pointer whitespace-nowrap underline`}
                    >
                      Or sign up free
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {messages.length === 0 && (
          <div className="tutor-empty">
          <div className="tutor-suggestions flex flex-wrap gap-2 mb-8 lg:max-w-3xl lg:mx-auto">
            {suggestedPrompts.map((prompt) => (
              <button
                key={prompt}
                onClick={() => {
                  setInput(prompt);
                  textareaRef.current?.focus();
                }}
                className={`px-3.5 py-2 rounded-full text-[13px] font-medium ${suggestedText} ${suggestedBg} ${suggestedHover} transition-colors duration-200 whitespace-nowrap border ${suggestedBorder} cursor-pointer`}
              >
                {prompt}
              </button>
            ))}
          </div>
          </div>
        )}

        <div className="space-y-8 lg:max-w-3xl lg:mx-auto">
          {messages.filter(msg => msg.id !== 'welcome-greeting').map((msg) => (
            <div
              key={msg.id}
              className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              {msg.role === 'user' ? (
                <div className={`max-w-[82%] lg:max-w-[68%] ${userBubbleBg} ${userTextColor} px-5 py-3 rounded-[22px] rounded-tr-md`}>
                  <FormattedMessage content={msg.content} isUser={true} theme={resolvedTheme} />
                </div>
              ) : (
                <div className="tutor-answer w-full">
                  {/* AI label row */}
                  <div className="flex items-center gap-2 mb-2.5">
                    <div className={`w-5 h-5 rounded-md flex items-center justify-center ${isDark ? 'bg-white/[0.07]' : 'bg-black/[0.05]'}`}>
                      <div className="w-3.5 h-3.5 flex items-center justify-center">
                        <i className={`ri-sparkling-line text-[11px] ${isDark ? 'text-[#A1A1A6]' : 'text-black/50'}`} />
                      </div>
                    </div>
                    <span className={`text-[11px] font-semibold uppercase tracking-wider ${isDark ? 'text-[#6E6E73]' : 'text-black/35'}`}>
                      LAVYN
                    </span>
                    <span className={`text-[11px] ${isDark ? 'text-[#48484F]' : 'text-black/20'}`}>
                      {msg.timestamp}
                    </span>
                  </div>
                  {/* AI content card */}
                  <div className={`tutor-answer-card ${aiContainerBg} border ${aiContainerBorder} rounded-2xl px-5 py-5 backdrop-blur-sm`}>
                    <FormattedMessage content={msg.content} isUser={false} theme={resolvedTheme} />
                  </div>
                  {msg.id !== 'welcome-greeting' && <button type="button" className="flex items-center gap-1.5 text-[11px] text-neutral-500 hover:text-neutral-400 py-2" aria-label="Copy tutor explanation" onClick={async () => { try { await navigator.clipboard.writeText(msg.content); setCopiedMessageId(msg.id); setCopyError(''); } catch { setCopyError('Copy is unavailable in this browser. You can select the explanation to copy it.'); } }}><i className={copiedMessageId === msg.id ? 'ri-check-line' : 'ri-file-copy-line'} />{copiedMessageId === msg.id ? 'Copied' : 'Copy explanation'}</button>}
                </div>
              )}
            </div>
          ))}

          {isTyping && (
            <div className="flex justify-start">
              <div className="w-full max-w-[92%] lg:max-w-[85%]">
                <div className="flex items-center gap-2 mb-2.5">
                  <div className={`w-5 h-5 rounded-md flex items-center justify-center ${isDark ? 'bg-white/[0.07]' : 'bg-black/[0.05]'}`}>
                    <div className="w-3.5 h-3.5 flex items-center justify-center">
                      <i className={`ri-sparkling-line text-[11px] ${isDark ? 'text-[#A1A1A6]' : 'text-black/50'}`} />
                    </div>
                  </div>
                  <span className={`text-[11px] font-semibold uppercase tracking-wider ${isDark ? 'text-[#6E6E73]' : 'text-black/35'}`}>
                    LAVYN
                  </span>
                </div>
                <div className={`${aiContainerBg} border ${aiContainerBorder} rounded-2xl px-5 py-4 backdrop-blur-sm`}>
                   <div className="flex flex-col gap-[9px]" role="status" aria-label="LAVYN is preparing an explanation">
                    {[0, 1, 2].map((i) => (
                      <span
                        key={i}
                         className={`h-2 rounded-full ${typingDot}`}
                        style={{
                          animation: `typingPulse 1.4s ease-in-out ${i * 0.25}s infinite`,
                           width: `${[85, 68, 43][i]}%`,
                        }}
                      />
                    ))}
                  </div>
                  <p className={`text-[11px] ${isDark ? 'text-[#48484F]' : 'text-black/20'} mt-2`}>Thinking about your question...</p>
                </div>
              </div>
            </div>
          )}

          {requestError && (
            <div className="rounded-2xl border border-red-400/15 bg-red-400/[0.06] px-4 py-3 text-[13px] leading-relaxed text-red-200">
              <div className="flex items-start gap-2">
                <i className="ri-error-warning-line mt-0.5" />
                <div className="flex-1">
                  <p className="font-semibold">LAVYN could not send that response.</p>
                  <p className="mt-1 text-red-200/75">{requestError}</p>
                  <button type="button" disabled={isTyping} className="mt-3 underline font-semibold" onClick={() => { const last = messages.filter((message) => message.role === 'user').at(-1); if (last) handleSendMessage(last.content, true); }}>Try again</button>
                </div>
              </div>
            </div>
          )}
          {copyError && <p role="status" className="text-xs text-neutral-500">{copyError}</p>}
        </div>
      </div>

      {/* Input Area */}
      <div className={`tutor-composer flex-shrink-0 px-4 pt-2 pb-4 z-10 ${bgClass} lg:px-8`}>
        <div
          className={`flex items-end gap-2 ${inputBg} rounded-[24px] px-3.5 py-2.5 border ${inputBorder} ${inputBorderFocus} transition-all duration-200 lg:max-w-3xl lg:mx-auto`}
        >
          <ExpandableMenu theme={resolvedTheme} />

          <textarea
            ref={textareaRef}
            aria-label="Message LAVYN"
            placeholder={!isRehydrated ? 'Restoring your account…' : isInputDisabled ? 'Sign in to keep learning…' : lessonContext ? 'Ask about this lesson…' : 'Ask a question, or share your working…'}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            rows={1}
            inputMode="text"
            autoCapitalize="sentences"
            autoCorrect="on"
            enterKeyHint="send"
            disabled={isInputDisabled || isTyping}
            className={`flex-1 min-w-0 bg-transparent text-base ${isDark ? 'text-white' : 'text-black'} ${placeholderColor} focus:outline-none resize-none py-1 max-h-[120px] leading-relaxed disabled:opacity-40`}
            style={{ touchAction: 'manipulation' }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
                e.preventDefault();
                handleSend();
              }
            }}
          />

          <button
            onClick={handleSend}
            aria-label="Send message"
            disabled={!input.trim() || isInputDisabled || isTyping}
            className={`w-8 h-8 rounded-full flex items-center justify-center cursor-pointer flex-shrink-0 mb-0.5 transition-colors duration-200 ${
              input.trim() && !isInputDisabled && !isTyping
                ? isDark
                  ? 'bg-white text-black'
                  : 'bg-black text-white'
                : sendInactiveBg
            }`}
            style={{ touchAction: 'manipulation' }}
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-up-line text-sm" />
            </div>
          </button>
        </div>

        <p className={`text-center text-[10px] ${disclaimerText} mt-2 tracking-wide`}>
          {lessonContext ? `Answering from ${lessonContext.lessonTitle}` : 'Check important answers with your class notes.'}
        </p>
      </div>

      {/* Demo Gate */}
      <DemoGate
        feature="ai_chat"
        open={!!activeGate}
        onClose={dismissGate}
      />

      <style>{`
        @keyframes typingPulse {
          0%, 100% { opacity: 0.2; transform: scale(1); }
          50% { opacity: 0.6; transform: scale(1.15); }
        }

      `}</style>
    </div>
  );
}